import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const categories = ["All", "Portraits", "Fashion", "Commercial", "Film & BTS", "Kids & Family", "Celebrations", "Outside the Studio"];

const galleryData = [
  { title: "Portfolio B&W 1", category: "Portraits", url: "/__l5e/assets-v1/290744de-dca8-4229-a09e-a643e2e2e079/Portfolio-BW-1-600x800.jpg" },
  { title: "Portfolio B&W 5", category: "Portraits", url: "/__l5e/assets-v1/a7df859d-ccc7-47f2-afb5-30501207a2db/Portfolio-BW-5-600x800.jpg" },
  { title: "Portfolio B&W 6", category: "Portraits", url: "/__l5e/assets-v1/6892e810-e6ce-4d33-85af-b2211efe1454/Portfolio-BW-6-1-600x800.jpg" },
  { title: "Birthday Celebration", category: "Celebrations", url: "/__l5e/assets-v1/80b4aa2d-3383-4680-8b4a-46193ee4e30c/MyStudio_Portfolio-Birthdays-600x800.jpg" },
  { title: "Outside the Studio", category: "Outside the Studio", url: "/__l5e/assets-v1/ba9a7a28-1f04-46b4-890b-8e5ae6bc4b83/MyStudio_Portfolio-OutsideStudio-600x800.jpg" },
  { title: "Studio Aid Setup", category: "Film & BTS", url: "/__l5e/assets-v1/f3e42060-b11f-4f89-be1c-d7636a1e2bd2/Studio_Aid-1-1-700x450.jpg" },
  { title: "Studio Prop Room", category: "Film & BTS", url: "/__l5e/assets-v1/d5a6d593-3e6e-4eeb-bfac-50c9a3602519/Studio_Prop-1.jpg" },
  { title: "Studio Set", category: "Film & BTS", url: "/__l5e/assets-v1/52a04afc-6d28-4293-acef-535f6689485b/Studio_Set-1-700x450.jpg" },
  { title: "Cedric Portrait", category: "Portraits", url: "/__l5e/assets-v1/73d82765-14f5-40bb-a355-19157676f2fd/Personnel-Cedric-700x853.jpg" },
  { title: "Tabitha Editorial", category: "Fashion", url: "/__l5e/assets-v1/85981944-86fd-4313-9c08-da9c6b1d30c4/tab-editorial-1-700x935.jpg" },
  { title: "Pentaxx Portrait", category: "Portraits", url: "/__l5e/assets-v1/7f7074a1-3580-4788-b7b5-c7d6426cdda9/pentaxx-guy-102-700x935.jpg" },
  { title: "Commercial B&W", category: "Commercial", url: "/__l5e/assets-v1/16b6870e-05fc-4606-9f2d-01301d5584e5/Portfolio-BW-3-700x450.jpg" },
];

const GalleryPage = () => {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const filtered = active === "All" ? galleryData : galleryData.filter((img) => img.category === active);

  const openLightbox = (i: number) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox((v) => (v !== null ? (v - 1 + filtered.length) % filtered.length : null));
  const next = () => setLightbox((v) => (v !== null ? (v + 1) % filtered.length : null));

  return (
    <div className="pt-20">
      <Helmet>
        <title>Studio Gallery | MyStudio Columbus, GA</title>
        <meta name="description" content="Portraits, fashion, commercial, and behind-the-scenes shots from MyStudio Columbus, GA — a premium photography and videography rental space." />
        <link rel="canonical" href="https://bookmystudiospace.com/gallery" />
        <meta property="og:title" content="Studio Gallery | MyStudio Columbus, GA" />
        <meta property="og:description" content="Portraits, fashion, commercial, and behind-the-scenes shots from MyStudio." />
        <meta property="og:url" content="https://bookmystudiospace.com/gallery" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "MyStudio Gallery",
          image: galleryData.map((g) => g.url),
        })}</script>
      </Helmet>
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-8">STUDIO SHOTS</h1>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`px-4 py-2 text-sm font-body transition-colors ${
                  active === cat ? "bg-primary text-primary-foreground" : "bg-panel border border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {filtered.map((img, i) => (
              <div key={`${img.title}-${i}`} className="break-inside-avoid group relative cursor-pointer overflow-hidden" onClick={() => openLightbox(i)}>
                <img src={img.url} alt={img.title} className="w-full group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div>
                    <h3 className="font-display tracking-wider">{img.title}</h3>
                    <span className="text-xs font-mono text-primary">{img.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Floating CTA */}
      <div className="fixed bottom-6 right-6 z-30">
        <Link to="/book">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold shadow-lg glow-amber px-6">
            Book a Shoot Like This
          </Button>
        </Link>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-foreground hover:text-primary">
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-foreground hover:text-primary">
            <ChevronRight className="w-8 h-8" />
          </button>
          <button onClick={closeLightbox} className="absolute top-4 right-4 p-2 text-foreground hover:text-primary">
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={filtered[lightbox].url} alt={filtered[lightbox].title} className="max-w-full max-h-[75vh] object-contain" />
            <div className="text-center mt-4">
              <h3 className="font-display tracking-wider text-lg">{filtered[lightbox].title}</h3>
              <span className="text-xs font-mono text-primary">{filtered[lightbox].category}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
